// src/components/Navbar.tsx
// Redesigned navbar:
//  - Desktop: top bar, but pages get proper padding so nothing is hidden
//  - Mobile:  bottom tab bar (Instagram-style) with 5 tabs + Extras sheet
//  - 5 main tabs: Home · Feed · Pravachan · Gungrahan · Smruti
//  - Everything else in Extras slide-up sheet

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useRef, useEffect, ReactNode } from 'react';

// ─── Icons (inline SVG so no extra deps) ─────────────────────
const Icon = {
  Home: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  Feed: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  Pravachan: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  ),
  Gungrahan: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
    </svg>
  ),
  Smruti: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
    </svg>
  ),
  More: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
    </svg>
  ),
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Logout: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  Report: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  Task: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
    </svg>
  ),
  Brahma: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
    </svg>
  ),
  Ashirwad: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
    </svg>
  ),
  Settings: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
  HowItWorks: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
  Gallery: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
    </svg>
  ),
};

// ─── Design tokens ────────────────────────────────────────────
const T = {
  ink:       "#0F0F0F",
  jade:      "#0F6E56",
  jadeMid:   "#1D9E75",
  saffron:   "#D97706",
  slate:     "#6B7280",
  border:    "rgba(0,0,0,0.08)",
  white:     "#FFFFFF",
  bg:        "#FAFAFA",
};

// ─── Desktop dropdown item ────────────────────────────────────
interface DropdownItem { label: string; path: string; icon: ReactNode }

function DesktopDropdown({ label, icon, items, id }: {
  label: string; icon: ReactNode; items: DropdownItem[]; id: string;
}) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => { clearTimeout(timer.current); setOpen(true); }}
      onMouseLeave={() => { timer.current = setTimeout(() => setOpen(false), 180); }}
    >
      <button style={{
        display: "flex", alignItems: "center", gap: 6,
        fontSize: 14, fontWeight: 500, color: T.slate,
        background: "none", border: "none", cursor: "pointer",
        padding: "7px 12px", borderRadius: 8,
        fontFamily: "inherit",
        transition: "color 0.15s",
      }}
        onMouseEnter={e => (e.currentTarget.style.color = T.ink)}
        onMouseLeave={e => (e.currentTarget.style.color = T.slate)}
      >
        {icon}
        {label}
        <span style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", display:"flex" }}>
          <Icon.ChevronDown />
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0,
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: 12, padding: "6px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
            minWidth: 200, zIndex: 200,
          }}
          onMouseEnter={() => clearTimeout(timer.current)}
          onMouseLeave={() => { timer.current = setTimeout(() => setOpen(false), 180); }}
        >
          {items.map(item => (
            <Link key={item.path} to={item.path} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "9px 12px", borderRadius: 8,
              fontSize: 13, fontWeight: 500, color: T.ink,
              textDecoration: "none",
              transition: "background 0.12s",
            }}
              onMouseEnter={e => (e.currentTarget.style.background = "#F3F4F6")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <span style={{ color: T.slate }}>{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Navbar ──────────────────────────────────────────────
export default function Navbar() {
  const { currentUser, logout } = useAuth() as any;
  const location = useLocation();
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Close extras when navigating
  useEffect(() => { setExtrasOpen(false); }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  // ── 5 main mobile tabs ──────────────────────────────────────
  const MAIN_TABS = [
    { id: "home",      label: "Home",      path: "/",             icon: <Icon.Home />,      auth: false },
    { id: "feed",      label: "Feed",      path: "/public-feed",  icon: <Icon.Feed />,      auth: false },
    { id: "pravachan", label: "Pravachan", path: "/pravachan",    icon: <Icon.Pravachan />, auth: true  },
    { id: "gungrahan", label: "Gungrahan", path: "/gungrahan",    icon: <Icon.Gungrahan />, auth: true  },
    { id: "smruti",    label: "Smruti",    path: "/smruti-storage", icon: <Icon.Smruti />, auth: true  },
  ];

  // ── Extras sheet items ──────────────────────────────────────
  const EXTRAS_SECTIONS = [
    {
      heading: "Reports",
      items: [
        { label: "GunGrahan Report",   path: "/report",              icon: <Icon.Report /> },
        { label: "Pravachan Report",   path: "/pravachan-report",    icon: <Icon.Report /> },
        { label: "BrahmnaSange Report",path: "/BrahmnaSangeReport",  icon: <Icon.Report /> },
        { label: "Ashirwad Report",    path: "/Ashirwad-report",     icon: <Icon.Report /> },
      ],
    },
    {
      heading: "Tools",
      items: [
        { label: "Daily Tasks",    path: "/daily-task",      icon: <Icon.Task />     },
        { label: "BrahmnaSange",   path: "/Brahmnasange",    icon: <Icon.Brahma />   },
        { label: "Ashirwad",       path: "/Ashirwad",        icon: <Icon.Ashirwad /> },
        { label: "Smruti Gallery", path: "/Smruti-Gallery",  icon: <Icon.Gallery />  },
        { label: "Satsang Todo",   path: "/daily-task",    icon: <Icon.Task />     },
      ],
    },
    {
      heading: "Account",
      items: [
        { label: "Settings",     path: "/Settings",      icon: <Icon.Settings />   },
        { label: "How it works", path: "/how-it-works",  icon: <Icon.HowItWorks /> },
      ],
    },
  ];

  // ── Desktop nav links ───────────────────────────────────────
  const DESKTOP_LINKS = [
    { label: "Feed",      path: "/public-feed",  auth: false },
    { label: "How it works", path: "/how-it-works", auth: false },
    ...(currentUser ? [
      { label: "Gungrahan",  path: "/gungrahan",   auth: true },
      { label: "Pravachan",  path: "/pravachan",   auth: true },
    ] : []),
  ];

  const DESKTOP_REPORTS: DropdownItem[] = [
    { label: "GunGrahan Report",    path: "/report",             icon: <Icon.Report /> },
    { label: "Pravachan Report",    path: "/pravachan-report",   icon: <Icon.Report /> },
    { label: "BrahmnaSange Report", path: "/BrahmnaSangeReport", icon: <Icon.Report /> },
  ];

  const DESKTOP_EXTRAS: DropdownItem[] = [
    { label: "Daily Tasks",    path: "/daily-task",     icon: <Icon.Task />     },
    { label: "BrahmnaSange",   path: "/Brahmnasange",   icon: <Icon.Brahma />   },
    { label: "Ashirwad",       path: "/Ashirwad",       icon: <Icon.Ashirwad /> },
    { label: "Smruti Gallery", path: "/Smruti-Gallery", icon: <Icon.Gallery />  },
    { label: "Satsang Todo",   path: "/daily-task",   icon: <Icon.Task />     },
    { label: "Settings",       path: "/Settings",       icon: <Icon.Settings /> },
  ];

  const linkStyle = (active: boolean): React.CSSProperties => ({
    fontSize: 14, fontWeight: 500,
    color: active ? T.ink : T.slate,
    textDecoration: "none",
    padding: "7px 12px", borderRadius: 8,
    transition: "color 0.15s",
    display: "inline-flex", alignItems: "center",
  });

  return (
    <>
      {/* ──────────────────────────────────────────────────────
          DESKTOP NAV — sticky top bar
          Pages must have paddingTop: 64px (or use the CSS class below)
      ────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.96)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${T.border}`,
        boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.06)" : "none",
        transition: "box-shadow 0.2s ease",
        display: "none", // shown via media query below
      }}
        className="ms-desktop-nav"
      >
        <div style={{
          maxWidth: 1080, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 60, padding: "0 24px",
        }}>
          {/* Brand */}
          <Link to="/" style={{
            fontFamily: "'Georgia','Times New Roman',serif",
            fontSize: 18, fontWeight: 700, color: T.ink,
            textDecoration: "none", letterSpacing: "-0.02em",
          }}>
            MySatsang
          </Link>

          {/* Centre links */}
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {DESKTOP_LINKS.map(l => (
              <Link key={l.path} to={l.path} style={linkStyle(isActive(l.path))}
                onMouseEnter={e => (e.currentTarget.style.color = T.ink)}
                onMouseLeave={e => (e.currentTarget.style.color = isActive(l.path) ? T.ink : T.slate)}
              >
                {l.label}
              </Link>
            ))}

            {currentUser && (
              <>
                <DesktopDropdown id="reports" label="Reports" icon={<Icon.Report />} items={DESKTOP_REPORTS} />
                <DesktopDropdown id="extras"  label="Extras"  icon={<Icon.More />}   items={DESKTOP_EXTRAS} />
              </>
            )}
          </div>

          {/* Right actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {currentUser ? (
              <button
                onClick={logout}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  fontSize: 14, fontWeight: 500, color: "#DC2626",
                  background: "none", border: "none", cursor: "pointer",
                  padding: "7px 12px", borderRadius: 8, fontFamily: "inherit",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => (e.currentTarget.style.background = "#FEF2F2")}
                onMouseLeave={e => (e.currentTarget.style.background = "none")}
              >
                <Icon.Logout /> Logout
              </button>
            ) : (
              <>
                <Link to="/login" style={{
                  fontSize: 14, fontWeight: 500, color: T.slate,
                  textDecoration: "none", padding: "7px 14px", borderRadius: 8,
                }}>
                  Sign in
                </Link>
                <Link to="/signup" style={{
                  fontSize: 14, fontWeight: 600, color: T.white,
                  background: T.ink, textDecoration: "none",
                  padding: "8px 16px", borderRadius: 8,
                }}>
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ──────────────────────────────────────────────────────
          MOBILE BOTTOM TAB BAR
          Only rendered when a user is logged in
      ────────────────────────────────────────────────────── */}
      {currentUser && (
        <div
          className="ms-mobile-nav"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(16px)",
            borderTop: `1px solid ${T.border}`,
            display: "none", // shown via media query
          }}
        >
          <div style={{
            display: "flex", justifyContent: "space-around",
            alignItems: "center", height: 62,
            paddingBottom: "env(safe-area-inset-bottom)",
          }}>
            {MAIN_TABS.filter(t => !t.auth || currentUser).map(tab => {
              const active = isActive(tab.path);
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    gap: 3, textDecoration: "none", minWidth: 48,
                    color: active ? T.ink : T.slate,
                    transition: "color 0.15s",
                  }}
                >
                  <div style={{
                    padding: "4px 12px", borderRadius: 20,
                    background: active ? "#F3F4F6" : "transparent",
                    transition: "background 0.15s",
                  }}>
                    {tab.icon}
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: active ? 600 : 400,
                    letterSpacing: "0.01em",
                  }}>
                    {tab.label}
                  </span>
                </Link>
              );
            })}

            {/* Extras tab */}
            <button
              onClick={() => setExtrasOpen(true)}
              style={{
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 3, background: "none", border: "none",
                cursor: "pointer", minWidth: 48,
                color: extrasOpen ? T.ink : T.slate,
              }}
            >
              <div style={{
                padding: "4px 12px", borderRadius: 20,
                background: extrasOpen ? "#F3F4F6" : "transparent",
              }}>
                <Icon.More />
              </div>
              <span style={{ fontSize: 10, fontWeight: 400 }}>More</span>
            </button>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────
          EXTRAS SLIDE-UP SHEET (mobile)
      ────────────────────────────────────────────────────── */}
      {/* Overlay */}
      {extrasOpen && (
        <div
          onClick={() => setExtrasOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 200,
            background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)",
          }}
        />
      )}

      {/* Sheet */}
      <div
        className="ms-mobile-nav"
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          zIndex: 201,
          background: T.white,
          borderRadius: "20px 20px 0 0",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.14)",
          transform: extrasOpen ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.3s cubic-bezier(0.32,0.72,0,1)",
          maxHeight: "85vh",
          overflowY: "auto",
          paddingBottom: "env(safe-area-inset-bottom)",
          display: "none", // shown via media query
        }}
      >
        {/* Handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 2,
          background: "#E5E7EB", margin: "14px auto 0",
        }} />

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px 10px",
          borderBottom: `1px solid ${T.border}`,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>More</span>
          <button
            onClick={() => setExtrasOpen(false)}
            style={{
              background: "#F3F4F6", border: "none", borderRadius: "50%",
              width: 32, height: 32, display: "flex", alignItems: "center",
              justifyContent: "center", cursor: "pointer",
            }}
          >
            <Icon.Close />
          </button>
        </div>

        {/* Sections */}
        <div style={{ padding: "12px 16px 24px" }}>
          {EXTRAS_SECTIONS.map(section => (
            <div key={section.heading} style={{ marginBottom: 20 }}>
              <p style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                textTransform: "uppercase", color: T.slate,
                margin: "0 0 8px", paddingLeft: 8,
              }}>
                {section.heading}
              </p>
              <div style={{
                background: T.bg, borderRadius: 14,
                overflow: "hidden", border: `1px solid ${T.border}`,
              }}>
                {section.items.map((item, i) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setExtrasOpen(false)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "13px 14px",
                      borderBottom: i < section.items.length - 1 ? `1px solid ${T.border}` : "none",
                      textDecoration: "none", color: T.ink,
                      fontSize: 14, fontWeight: 500,
                      transition: "background 0.12s",
                    }}
                    onTouchStart={e => (e.currentTarget.style.background = "#F3F4F6")}
                    onTouchEnd={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <span style={{ color: T.slate }}>{item.icon}</span>
                    {item.label}
                    <svg style={{ marginLeft: "auto", color: "#D1D5DB" }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* Logout */}
          <button
            onClick={() => { (logout as any)(); setExtrasOpen(false); }}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "13px 14px", borderRadius: 12,
              background: "#FEF2F2", border: "1px solid #FECACA",
              color: "#DC2626", fontSize: 14, fontWeight: 500,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <Icon.Logout /> Logout
          </button>
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────
          GLOBAL STYLES
          Inject once — handles responsive show/hide and
          gives every page the correct top/bottom padding.
      ────────────────────────────────────────────────────── */}
      <style>{`
        /* Desktop nav + content offset */
        @media (min-width: 768px) {
          .ms-desktop-nav { display: block !important; }
          .ms-mobile-nav  { display: none   !important; }
          /* All page content should clear the 60px desktop nav */
          body { padding-top: 60px; }
        }

        /* Mobile: bottom bar + content offset */
        @media (max-width: 767px) {
          .ms-desktop-nav { display: none   !important; }
          .ms-mobile-nav  { display: block  !important; }
          /* Push content up from the 62px bottom tab bar */
          body { padding-top: 0; padding-bottom: 62px; }
        }
      `}</style>
    </>
  );
}